import { AvStateType } from "@minervaproject/avsubsystem-core";
import {
  differenceInMinutes,
  isAfter,
  isBefore,
  isFuture,
  isPast,
  subHours,
  subMinutes,
  subMonths,
} from "date-fns";
import { toPairs, uniq } from "lodash";

import {
  ClassMonitoringStatsType,
  ClassStatusType,
  ClassroomInfoType,
  ClassroomSliceStateType,
  MonitoringEventStatsType,
  MonitoringSliceStateType,
  RecordingStateType,
} from "../types";
import {
  LevelType,
  MonitoringEventType,
  MonitoringEventTypeType,
  TargetUserMonitoringEventType,
} from "../types/monitoring_event";

export const MONITORING_EVENT_TYPES: Record<
  MonitoringEventTypeType,
  { level: LevelType; description: string; needsAvState?: boolean }
> = {
  [MonitoringEventTypeType.ADMIN_CHANGE_AV_SERVICE]: {
    level: LevelType.IMPORTANT,
    description: "Admin switches AV service",
    needsAvState: true,
  },
  [MonitoringEventTypeType.ADMIN_CHANGE_LP_TYPE]: {
    level: LevelType.IMPORTANT,
    description: "Admin changed class lesson plan type/modality",
  },
  [MonitoringEventTypeType.ADMIN_CHANGE_MAX_UPDATED_VIDEOS]: {
    level: LevelType.IMPORTANT,
    description: "Admin changed max videos updated during shuffles/filter changes",
  },
  [MonitoringEventTypeType.ADMIN_CHANGE_MAX_VIDEOS_IN_PARTICIPANT_BAR]: {
    level: LevelType.IMPORTANT,
    description: "Admin changed max videos in participant bar",
  },
  [MonitoringEventTypeType.ADMIN_FORCE_AUDIO_ONLY]: {
    level: LevelType.IMPORTANT,
    description: "An admin/support staff changed a user's bandwidth_mode to 'audio-only'",
  },
  [MonitoringEventTypeType.ADMIN_FORCE_HIGH_BANDWIDTH]: {
    level: LevelType.IMPORTANT,
    description: "An admin/support staff changed a user's bandwidth_mode to 'high'",
  },
  [MonitoringEventTypeType.ADMIN_FORCE_LIVE_CAPTIONS_CHANGE]: {
    level: LevelType.IMPORTANT,
    description: "An admin/support staff changed a user's enable_live_captions",
  },
  [MonitoringEventTypeType.ADMIN_FORCE_RELOAD_AV]: {
    level: LevelType.IMPORTANT,
    description: "An admin/support staff forced one user to restart AV connections",
  },
  [MonitoringEventTypeType.ADMIN_FORCE_RELOAD_PAGE]: {
    level: LevelType.IMPORTANT,
    description: "An admin/support staff forced one user to reload their page",
  },
  [MonitoringEventTypeType.ADMIN_HARD_RESET_CLASS]: {
    level: LevelType.IMPORTANT,
    description: "Admin hard resets class",
    needsAvState: true,
  },
  [MonitoringEventTypeType.ADMIN_RELOAD_PAGE_FOR_EVERYBODY]: {
    level: LevelType.IMPORTANT,
    description: "Admin reloads page for everybody",
    needsAvState: true,
  },
  [MonitoringEventTypeType.ADMIN_SOFT_RESET_CLASS]: {
    level: LevelType.IMPORTANT,
    description: "Admin soft resets class",
    needsAvState: true,
  },
  [MonitoringEventTypeType.AV_CHECKIN]: {
    level: LevelType.STATS,
    description: "AV store status snapshot",
  },
  [MonitoringEventTypeType.AV_CONNECTION_QUALITY_CHANGED]: {
    level: LevelType.IMPORTANT,
    description: "The A/V connection quality changed",
    needsAvState: true,
  },
  [MonitoringEventTypeType.AV_REFRESH]: {
    level: LevelType.IMPORTANT,
    description: "The A/V for the user was refreshed",
    needsAvState: true,
  },
  [MonitoringEventTypeType.BUG_REPORT_SUBMITTED]: {
    level: LevelType.IMPORTANT,
    description: "User submits a bug report",
    needsAvState: true,
  },
  [MonitoringEventTypeType.CHANGE_BANDWIDTH_MODE]: {
    level: LevelType.IMPORTANT,
    description: "User switches their own bandwidth mode",
    needsAvState: true,
  },
  [MonitoringEventTypeType.CHANGE_CLASS_MODALITY]: {
    level: LevelType.IMPORTANT,
    description: "User changes class modality",
  },
  [MonitoringEventTypeType.CHANGE_CLASS_PTT_MODE]: {
    level: LevelType.IMPORTANT,
    description: "User switches PTT mode for entire class",
  },
  [MonitoringEventTypeType.DEVICES_SELECTED]: {
    level: LevelType.IMPORTANT,
    description: "Selected devices have changed",
  },
  [MonitoringEventTypeType.DEVICES_ERROR]: {
    level: LevelType.IMPORTANT,
    description: "Error selecting devices",
    needsAvState: true,
  },
  [MonitoringEventTypeType.PRECLASS_CHECK_FAIL]: {
    level: LevelType.IMPORTANT,
    description: "One or more tests failed in the preclass check",
    needsAvState: true,
  },
  [MonitoringEventTypeType.PRECLASS_CHECK_STARTED]: {
    level: LevelType.IMPORTANT,
    description: "User starts the preclass check",
  },
  [MonitoringEventTypeType.PRECLASS_CHECK_SUCCESS]: {
    level: LevelType.IMPORTANT,
    description: "All tests passed in the preclass check",
  },
  [MonitoringEventTypeType.ENTER_CLASS]: {
    level: LevelType.IMPORTANT,
    description: "User enters class",
  },
  [MonitoringEventTypeType.ENTER_CLASS_NEW_TAB]: {
    level: LevelType.IMPORTANT,
    description: "User enters class in an other tab or window",
  },
  [MonitoringEventTypeType.RELOAD_STREAM]: {
    level: LevelType.IMPORTANT,
    description: "User reloaded another user's stream",
    needsAvState: true,
  },
  [MonitoringEventTypeType.STREAM_STARTED]: {
    level: LevelType.IMPORTANT,
    description: "The user has successfully published a stream",
  },
  [MonitoringEventTypeType.STREAM_FAILED]: {
    level: LevelType.IMPORTANT,
    description: "The AV service detected a failure in a stream",
    needsAvState: true,
  },
  [MonitoringEventTypeType.EXTENSION_STATS]: {
    level: LevelType.STATS,
    description: "Extension stats",
  },
  [MonitoringEventTypeType.FAILED_API_REQUEST]: {
    level: LevelType.IMPORTANT,
    description: "An API request failed",
  },
  [MonitoringEventTypeType.FAILED_CONNECTION_ALERT]: {
    level: LevelType.IMPORTANT,
    description: "User gets a failed connection alert caused by a fatal error when connecting",
    needsAvState: true,
  },
  [MonitoringEventTypeType.FORCED_BANDWIDTH_MODE_CHANGE]: {
    level: LevelType.IMPORTANT,
    description: "User receives a pubsub message telling them to change their bandwidth mode",
  },
  [MonitoringEventTypeType.HARDWARE_LOAD]: {
    level: LevelType.STATS,
    description: "Hardware load stats",
  },
  [MonitoringEventTypeType.LEAVE_CLASS]: {
    level: LevelType.IMPORTANT,
    description: "User leaves class",
  },
  [MonitoringEventTypeType.LEAVE_CLASS_NEW_TAB]: {
    level: LevelType.IMPORTANT,
    description: "User was kicked out because they entered the classroom in a second tab or window",
  },
  [MonitoringEventTypeType.OPEN_PRESENTATION_VIEW]: {
    level: LevelType.INFO,
    description: "User opens presentation view",
  },
  [MonitoringEventTypeType.PING_TIMEOUT]: {
    level: LevelType.INFO,
    description: "User websocket ping timed out: connection terminated",
    needsAvState: true,
  },
  [MonitoringEventTypeType.POOR_CONNECTION_INDICATOR_CHANGED]: {
    level: LevelType.INFO,
    description: "The poor connection indicator has changed",
    needsAvState: true,
  },
  [MonitoringEventTypeType.PUBSUB_CONNECTED]: {
    level: LevelType.INFO,
    description: "User connects to the pubsub server",
  },
  [MonitoringEventTypeType.PUBSUB_DISCONNECTED]: {
    level: LevelType.INFO,
    description: "User disconnects from the pubsub server",
    needsAvState: true,
  },
  [MonitoringEventTypeType.PUBSUB_REQUEST_SYNC]: {
    level: LevelType.IMPORTANT,
    description: "request-sync message sent to pubsub server",
  },
  [MonitoringEventTypeType.PUBSUB_STATS]: {
    level: LevelType.STATS,
    description: "Pubsub stats collection",
    needsAvState: true,
  },
  [MonitoringEventTypeType.SHOW_BREAKOUT_CONTROL_ROOM]: {
    level: LevelType.INFO,
    description: "User enters the breakout control room",
    needsAvState: true,
  },
  [MonitoringEventTypeType.SHOW_BREAKOUT_OVERVIEW_ROOM]: {
    level: LevelType.INFO,
    description: "User enters the breakout overview room (presentation view)",
    needsAvState: true,
  },
  [MonitoringEventTypeType.SHOW_BREAKOUT_ROOM]: {
    level: LevelType.INFO,
    description: "User enters the breakout room",
    needsAvState: true,
  },
  [MonitoringEventTypeType.SHOW_MAIN_CLASSROOM]: {
    level: LevelType.INFO,
    description: "User enters the main classroom",
    needsAvState: true,
  },
  [MonitoringEventTypeType.START_MANUAL_RECORDING]: {
    level: LevelType.INFO,
    description: "User clicks on the Start Recording button inside the classroom",
  },
  [MonitoringEventTypeType.STOP_MANUAL_RECORDING]: {
    level: LevelType.INFO,
    description: "User clicks on the Stop Recording button inside the classroom",
  },
  [MonitoringEventTypeType.TIMELINE_SEGMENT]: {
    level: LevelType.INFO,
    description: "User clicks on a timeline segment",
  },
  [MonitoringEventTypeType.TIMELINE_SEGMENT_SECONDARY_ACTION]: {
    level: LevelType.INFO,
    description: "User clicks on one of the buttons under a timeline segment",
  },
  [MonitoringEventTypeType.TOGGLE_BACKGROUND_BLUR]: {
    level: LevelType.INFO,
    description: "User toggles background blur",
  },
  [MonitoringEventTypeType.TOGGLE_CAMERA]: {
    level: LevelType.IMPORTANT,
    description: "User toggles camera",
    needsAvState: true,
  },
  [MonitoringEventTypeType.TOGGLE_LIVE_CAPTIONS]: {
    level: LevelType.INFO,
    description: "User clicks the live captions toggle button in the Settings menu",
  },
  [MonitoringEventTypeType.TOGGLE_MIC]: {
    level: LevelType.INFO,
    description: "User toggles mic",
    needsAvState: true,
  },
};

export function roundTo2Decimals(x: number) {
  return Math.round(x * 100) / 100;
}

export function getClassStartTime(classInfo: ClassroomInfoType) {
  if (!classInfo.date) return null;
  return new Date(classInfo.date);
}

export function getClassEndTime(classInfo: ClassroomInfoType) {
  if (!classInfo.end_time) return null;
  return new Date(classInfo.end_time);
}

export function isFutureClass(classInfo: ClassroomInfoType) {
  const classStart = getClassStartTime(classInfo);
  return classStart && isFuture(classStart);
}

export function getClassDurationInMinutes(classInfo: ClassroomInfoType) {
  const classStart = getClassStartTime(classInfo);
  const classEnd = getClassEndTime(classInfo);
  if (!classStart || !classEnd) return null;

  return differenceInMinutes(classEnd, classStart);
}

export function getClassStatus(
  classInfo: ClassroomInfoType,
  currentlyPresentUserCount: number,
): ClassStatusType {
  const classStart = getClassStartTime(classInfo);
  const classEnd = getClassEndTime(classInfo);
  if (!classStart || !classEnd) return "";

  if (isPast(classStart) && isFuture(classEnd)) return "In Progress";
  if (currentlyPresentUserCount > 0) return "Active";
  if (isPast(classEnd)) return "Completed";
  if (isPast(subHours(classStart, 1))) {
    // Starting in less than an hour
    return "Starting soon";
  }
  return "Not yet started";
}

export function getRecordingState(
  classInfo: ClassroomInfoType,
  recordingSessionId: string | undefined,
  recordingState: RecordingStateType,
) {
  // TODO: Compared to the Forum Classic version, this is missing the recordingSessionUuid,
  // which is used to generate a link to the recording dashboard. This version also doesn't
  // warn the user if the session was partially recorded.
  const classStart = getClassStartTime(classInfo);
  const classEnd = getClassEndTime(classInfo);
  if (!classStart || !classEnd)
    return {
      state: "",
      title: "This class does not have a start or end time",
      danger: false,
    };

  const shouldRecord = classInfo.should_record_session;

  if (isFuture(classStart)) {
    if (shouldRecord)
      return {
        state: "REC",
        title: "This class is scheduled to be recorded",
        danger: false,
      };
    return {
      state: "NOREC",
      title: "This class is not scheduled to be recorded",
      danger: false,
    };
  }

  if (isPast(classEnd)) {
    if (shouldRecord) {
      if (recordingSessionId)
        return {
          state: "REC",
          title: "This class was scheduled to be recorded and was recorded",
          danger: false,
        };
      return {
        state: "NOREC",
        title: "This class was scheduled to be recorded but WAS NOT RECORDED",
        danger: true,
      };
    }
    if (recordingSessionId)
      return {
        state: "REC",
        title: "This class was not scheduled to be recorded but was at least partly recorded",
        danger: false,
      };
    return {
      state: "NOREC",
      title: "This class was not scheduled to be recorded and was not recorded",
      danger: false,
    };
  }

  const prefix = `This class was${shouldRecord ? "" : " not"} scheduled to be recorded and`;
  const titleMap = {
    [RecordingStateType.Preparing]: `${prefix} is preparing to record`,
    [RecordingStateType.Starting]: `${prefix} the recording will start soon`,
    [RecordingStateType.Recording]: `${prefix} is being recorded`,
    [RecordingStateType.NotRecording]: `${prefix} IS NOT BEING RECORDED`,
  };
  if (shouldRecord) {
    if (recordingSessionId)
      return {
        state: recordingState,
        title: titleMap[recordingState],
        danger: recordingState === RecordingStateType.NotRecording,
      };
    return {
      state: "NOREC",
      title: "This class was scheduled to be recorded and IS NOT BEING RECORDED",
      danger: true,
    };
  }
  if (recordingSessionId)
    return {
      state: recordingState,
      title: titleMap[recordingState],
      danger: false,
    };
  return {
    state: "NOREC",
    title: "This class was not scheduled to be recorded and is not being recorded",
    danger: false,
  };
}

export function nAttendees(
  classInfo: ClassroomInfoType,
  currentlyPresentUserCount: number,
  oncePresentUserCount: number,
  forStats = false,
) {
  const classStatus = getClassStatus(classInfo, currentlyPresentUserCount);
  // When we calculate class stats (for example, mean page loads), we normalize by number of attendees.
  // For active classes, since we use all MonitoringEvents (not just in class MonitoringEvents), we should normalize by
  // the number of users once present.
  if ((forStats && classStatus === "Active") || classStatus === "Completed") {
    return oncePresentUserCount || 0;
  }
  return currentlyPresentUserCount;
}

export function getNonObserverUsers(classroomState: ClassroomSliceStateType) {
  return Object.values(classroomState.users).filter(
    (user) => !["observer", ""].includes(user.role),
  );
}

export function getNonObserverUserCount(classroomState: ClassroomSliceStateType) {
  return getNonObserverUsers(classroomState).length;
}

export function getCurrentlyPresentUserCount(classroomState: ClassroomSliceStateType) {
  return getNonObserverUsers(classroomState).filter((user) => user.is_present).length;
}

export function getOncePresentUserCount(
  classroomState: ClassroomSliceStateType,
  monitoringState: MonitoringSliceStateType | undefined,
) {
  const monitoring_events = monitoringState?.monitoring_events || {};
  const nonObserverUserIds = getNonObserverUsers(classroomState).map((user) => user.id);
  return uniq(
    Object.values(monitoring_events)
      .filter((event) => event.type === MonitoringEventTypeType.ENTER_CLASS)
      .map((event) => event.userId)
      .filter((userId) => nonObserverUserIds.includes(userId)),
  ).length;
}

export function computeMonitoringEventStats(
  classroomState: ClassroomSliceStateType,
  monitoringEvents: MonitoringEventType[],
  oncePresentUserCount: number,
  doNotFilterByTime = false,
  useTargetUserIds = false,
): MonitoringEventStatsType {
  // First determine if class is active, in progress, past or upcoming
  const classInfo = classroomState.info;
  const nonObserverUserIds = getNonObserverUsers(classroomState).map((user) => user.id);
  const currentlyPresentUserCount = getCurrentlyPresentUserCount(classroomState);
  const classStatus = getClassStatus(classInfo, currentlyPresentUserCount);

  const durationInMinutes = getClassDurationInMinutes(classInfo);
  const nParticipants = nAttendees(
    classInfo,
    currentlyPresentUserCount,
    oncePresentUserCount,
    true,
  );

  if (!nParticipants || !durationInMinutes) {
    return {
      count: 0,
      nAffectedUsers: 0,
      mean: null,
      percentAffectedUsers: null,
      formattedMean: "-",
    };
  }

  // Filter monitoring events.
  // For active classes (classes that are not currently in progress but have present users in them), we count all events.
  // For other classes, we only count events that happened during class or right before class.
  const startTime = getClassStartTime(classInfo);
  const endTime = getClassEndTime(classInfo);
  const startTimeMinus10 = startTime && subMinutes(startTime, 10);
  const filteredEvents = monitoringEvents.filter(({ timestamp, userId, contextData }) => {
    const targetUserId = "targetUserId" in contextData ? contextData.targetUserId || "" : "";
    if (useTargetUserIds && targetUserId && !nonObserverUserIds.includes(targetUserId))
      return false;
    if (!useTargetUserIds && !nonObserverUserIds.includes(userId)) return false;
    if (doNotFilterByTime) return true;
    const createdOn = new Date(timestamp);
    if (
      classStatus !== "Active" &&
      startTimeMinus10 &&
      endTime &&
      (isBefore(createdOn, startTimeMinus10) || isAfter(createdOn, endTime))
    )
      return false;
    return true;
  });
  // Compute number of events and number of affected users
  const nEvents = filteredEvents.length;
  const affectedUsers = uniq(
    filteredEvents.map((event) =>
      useTargetUserIds
        ? (event as TargetUserMonitoringEventType).contextData.targetUserId || ""
        : event.userId,
    ),
  );
  const nAffectedUsers = affectedUsers.length;

  // Compute mean occurrence of events per user per hour
  const mean = nEvents / nParticipants / (durationInMinutes / 60);
  const percentAffectedUsers = (nAffectedUsers / nParticipants) * 100;
  return {
    count: nEvents,
    nAffectedUsers,
    mean,
    percentAffectedUsers,
    formattedMean:
      nEvents === 0 || nParticipants <= 5
        ? `${roundTo2Decimals(mean)}`
        : `${roundTo2Decimals(mean)} (${Math.round(percentAffectedUsers)}% of users)`,
  };
}

export function computeMonitoringStats(
  classroomState: ClassroomSliceStateType | undefined,
  monitoringState: MonitoringSliceStateType | undefined,
): ClassMonitoringStatsType | Record<string, never> {
  if (!classroomState || !classroomState.info.date) {
    console.error("Classroom state or classroom date is missing");
    return {};
  }
  const {
    active_breakout_session_id,
    av_service,
    info,
    licode_server,
    recording_session_id,
    recording_state,
  } = classroomState;
  const currentlyPresentUserCount = getCurrentlyPresentUserCount(classroomState);
  const nonObserverCount = getNonObserverUserCount(classroomState);
  const oncePresentUserCount = getOncePresentUserCount(classroomState, monitoringState);
  const teachers = Object.values(classroomState.users).filter((user) => user.role === "teacher");
  const output = {
    ...info,
    activeBreakoutSessionId: active_breakout_session_id,
    avService: av_service,
    licodeServer: licode_server,
    nonObserverCount,
    currentlyPresentUserCount,
    oncePresentUserCount,
    recordingSessionId: recording_session_id,
    recordingState: recording_state,
    teachers,
  };
  const allMonitoringEvents = Object.values(monitoringState?.monitoring_events || {});
  const { count: nBugReports } = computeMonitoringEventStats(
    classroomState,
    allMonitoringEvents.filter(({ type }) => type === MonitoringEventTypeType.BUG_REPORT_SUBMITTED),
    oncePresentUserCount,
    true,
    false,
  );
  const pageLoadStats = computeMonitoringEventStats(
    classroomState,
    allMonitoringEvents.filter(({ type }) => type === MonitoringEventTypeType.ENTER_CLASS),
    oncePresentUserCount,
    false,
    false,
  );
  const supportActionStats = computeMonitoringEventStats(
    classroomState,
    allMonitoringEvents.filter(({ type }) => type.startsWith("admin")),
    oncePresentUserCount,
    true,
    true,
  );
  return { ...output, nBugReports, pageLoadStats, supportActionStats };
}

function buildFacetQueryString({ classId, userId }: { classId: string; userId?: string }) {
  const joined = toPairs({ classId, userId })
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    .filter(([_, val]) => !!val)
    .map(([key, val]) => `@${key}:${val}`)
    .join(" ");

  return encodeURIComponent(joined);
}

function buildDatadogQueryUrl(query: string) {
  const now = Date.now();
  const oneMonthAgo = subMonths(now, 1).getTime();
  return `https://app.datadoghq.com/logs?&query=${query}&from_ts=${oneMonthAgo}&to_ts=${now}&stream_sort=desc&live=true`;
}

export function buildDatadogFacetedUrl({ classId, userId }: { classId: string; userId?: string }) {
  const queryString = buildFacetQueryString({ classId, userId });
  return buildDatadogQueryUrl(queryString);
}

// Produces a digest with the most revelant information from the AV state
export function getReducedAvState(state: AvStateType) {
  const copiedState = JSON.parse(JSON.stringify(state));
  // devices list is added in devices-selected
  delete copiedState.device_list;
  for (const stream of Object.keys(copiedState.streams)) {
    delete copiedState.streams[stream].stats.connection;
  }
  // We only keep transport info - the rest is in stream-started
  for (const stream of Object.keys(state.streams)) {
    const connection = state.streams[stream].stats.connection;
    copiedState.streams[stream].stats.connection = {};
    if (connection) {
      for (const connInfo of Object.keys(connection)) {
        if (connection[connInfo].type === "transport") {
          copiedState.streams[stream].stats.connection[connInfo] = connection[connInfo];
          break;
        }
      }
    }
  }
  return copiedState;
}
